import { Box, Card, Typography } from "@mui/material";
import { type FC, useContext, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { SearchTextField } from "@packsize/components";

import { FailedRetry } from "@/components/failed-retry";
import { ProductionContext } from "@/contexts/production";
import { useDebouncedSearch } from "@/hooks/use-debounced-search";

import { useAvailableBatches } from "../query/use-available-batches";

import { AvailableBatchesTable } from "./available-batches-table";

export const AvailableBatchesCard: FC = () => {
	const intl = useIntl();
	const { currentProductionGroup, currentMachineGroup } = useContext(ProductionContext);
	const hasMultipleMachineGroups = (currentProductionGroup?.configuredMachineGroups?.length ?? 0) > 1;
	const [pageNumber, setPageNumber] = useState(1);
	const [pageSize, setPageSize] = useState(10);

	const { inputValue, filter, handleInputChange, handleSearch } = useDebouncedSearch<null>({ emptyValue: null });

	let machineGroupId: string | null = null;
	if (!hasMultipleMachineGroups && currentMachineGroup) machineGroupId = currentMachineGroup.id;

	const { pageOfBatches, isLoading, refetch, error } = useAvailableBatches({
		productionGroupId: currentProductionGroup?.id || "",
		pipelineId: currentProductionGroup?.pipelineId || "",
		machineGroupId,
		pageNumber,
		pageSize,
		filter,
	});

	if (error) {
		return <FailedRetry retry={refetch} />;
	}

	return (
		<Card sx={{ display: "flex", flexDirection: "column", flexGrow: 1, height: "100%" }}>
			<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: 2 }}>
				<Typography variant="body2">
					<FormattedMessage
						id="Available Orders In {pgAlias} ({totalResults})"
						defaultMessage="Available Orders In {pgAlias} ({totalResults})"
						values={{ pgAlias: currentProductionGroup?.alias, totalResults: pageOfBatches?.totalResults || 0 }}
					/>
				</Typography>
				<SearchTextField
					size="small"
					value={inputValue}
					onChange={handleInputChange}
					placeholder={intl.formatMessage({
						id: "Title or Trigger Value...",
						defaultMessage: "Title or Trigger Value...",
					})}
					onSubmit={handleSearch}
					autoSelectOnFocus
				/>
			</Box>
			<AvailableBatchesTable
				batches={pageOfBatches?.results || []}
				pageNumber={pageNumber}
				setPageNumber={setPageNumber}
				pageSize={pageSize}
				setPageSize={setPageSize}
				isLoading={isLoading}
				totalResults={pageOfBatches?.totalResults || 0}
				filter={filter}
			/>
		</Card>
	);
};
